import React, { useEffect, useState } from 'react';
import { BrandLogo } from './BrandLogo';
import { SfiLogo } from './SfiLogo';

interface MobileBottomSheetProps {
  /** Compact label shown above the title (mill state, drive time, etc.). */
  peekEyebrow?: string;
  /** Title shown in the bar — typically the active layer or page name. */
  peekTitle: string;
  /** Optional badge rendered to the right of the brand lockup (e.g. the
      DEMO pill). Lets the peek bar carry chrome that the panel body
      would otherwise duplicate on mobile. */
  peekBadge?: React.ReactNode;
  /** Initial expanded state for uncontrolled mode. Defaults to `true` so
      users land in panel mode and immediately see the data. Ignored when
      the parent supplies `expanded` (controlled mode). */
  initiallyExpanded?: boolean;
  /** Controlled expanded state. When provided, the sheet's open/closed
      state is fully driven by the parent — pair with `onExpandedChange`
      to receive user-initiated toggles. Use this when external events
      need to drive collapse/expand (e.g. auto-collapsing into map mode
      when the user activates the per-crown native view). */
  expanded?: boolean;
  /** Callback fired when the user taps the Map button or the peek bar.
      Required for controlled mode; optional in uncontrolled mode for
      side-effects on transitions. */
  onExpandedChange?: (expanded: boolean) => void;
  children: React.ReactNode;
  /** Z-index. Defaults to 1100 — above Leaflet panes (cap 700) and the
      MapZoomControl / BasemapSwitch stack (z=1000), below modals (2000).
      The expanded panel must outrank the corner control stack so the
      basemap switch + zoom buttons don't poke through the header. */
  zIndex?: number;
}

/**
 * Mobile-only two-mode panel. Renders nothing on `md+`. There is no
 * half-and-half state — trying to show the map and panel simultaneously
 * left both feeling cramped, and the half-collapse chevron was easy to
 * miss. Instead:
 *
 * - **Panel mode** (expanded): sheet covers the full viewport. Header
 *   shows a prominent **Map** button so the action is unambiguous.
 * - **Map mode** (collapsed): a single peek bar pinned to the bottom of
 *   the viewport. The map fills the entire space above. The bar itself
 *   is a tap target with a high-contrast **Details ▴** pill so it's
 *   obvious how to get back.
 *
 * iOS-safe: `100dvh` is implicit (`fixed inset-0`), and we honor
 * `env(safe-area-inset-top)` / `env(safe-area-inset-bottom)` so the
 * notch / home-indicator don't clip content or the tap target.
 */
// Compact CTrees + SFI lockup used as the persistent brand mark in the
// peek bar, mirroring the desktop panel header. Sized small so the
// title and toggle button still get the bulk of the bar's width on
// narrow phones.
const CoBrandLockup: React.FC = () => (
  <div className="flex items-center gap-1.5 flex-shrink-0">
    <BrandLogo size={20} />
    <span aria-hidden="true" className="h-4 w-px bg-figma-ink-mute/40" />
    <SfiLogo size={20} />
  </div>
);

export const MobileBottomSheet: React.FC<MobileBottomSheetProps> = ({
  peekEyebrow,
  peekTitle,
  peekBadge,
  initiallyExpanded = true,
  expanded: controlledExpanded,
  onExpandedChange,
  children,
  zIndex = 1100,
}) => {
  const isControlled = controlledExpanded !== undefined;
  const [internalExpanded, setInternalExpanded] = useState(initiallyExpanded);
  const expanded = isControlled ? controlledExpanded : internalExpanded;
  const setExpanded = (next: boolean) => {
    if (!isControlled) setInternalExpanded(next);
    onExpandedChange?.(next);
  };

  // Lock body scroll while the panel covers the viewport so the page
  // underneath doesn't drift when the user scrolls inside the panel.
  // Restored automatically on collapse / unmount.
  useEffect(() => {
    if (!expanded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [expanded]);

  if (expanded) {
    return (
      <div
        className="md:hidden fixed inset-0 bg-figma-panel flex flex-col"
        style={{ zIndex }}
        role="dialog"
        aria-label={peekTitle}
      >
        <div
          className="flex-shrink-0 flex items-center gap-3 px-4 py-3 bg-figma-panel border-b border-white/10"
          style={{ paddingTop: 'calc(env(safe-area-inset-top) + 12px)' }}
        >
          <CoBrandLockup />
          {peekBadge && <div className="flex-shrink-0">{peekBadge}</div>}
          <div className="flex-1 flex flex-col items-start min-w-0">
            {peekEyebrow && (
              <span className="text-[10px] font-roboto-mono uppercase tracking-[0.10em] text-figma-ink-mute truncate w-full">
                {peekEyebrow}
              </span>
            )}
            <span className="text-figma-text font-roboto font-bold text-[14px] uppercase tracking-[0.08em] truncate w-full">
              {peekTitle}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="flex items-center gap-1.5 px-3 h-11 rounded-lg bg-figma-card border border-figma-lime/50 text-figma-lime active:bg-figma-lime/10 transition-colors flex-shrink-0"
            aria-label="View map"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M9 4v16M15 4v16M3 8l6-2 6 2 6-2v12l-6 2-6-2-6 2V8z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="text-xs font-roboto-mono uppercase tracking-wider font-bold">
              Map
            </span>
          </button>
        </div>
        <div
          className="flex-1 overflow-y-auto overscroll-contain"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          {children}
        </div>
      </div>
    );
  }

  // Map mode — peek bar pinned to the bottom. The whole bar is a single
  // button so taps anywhere reopen the panel; the lime "Details ▴" pill
  // on the right makes the action unambiguous.
  return (
    <button
      type="button"
      onClick={() => setExpanded(true)}
      className="md:hidden fixed bottom-0 left-0 right-0 bg-figma-panel border-t border-figma-lime/30 shadow-[0_-8px_24px_rgba(0,0,0,0.45)] flex items-center gap-3 px-4 active:bg-figma-card transition-colors text-left touch-manipulation"
      style={{
        zIndex,
        paddingTop: '14px',
        paddingBottom: 'calc(14px + env(safe-area-inset-bottom))',
      }}
      aria-label={`Open ${peekTitle} details`}
    >
      <CoBrandLockup />
      {peekBadge && <div className="flex-shrink-0">{peekBadge}</div>}
      <div className="flex-1 flex flex-col items-start min-w-0">
        {peekEyebrow && (
          <span className="text-[10px] font-roboto-mono uppercase tracking-[0.10em] text-figma-ink-mute truncate w-full">
            {peekEyebrow}
          </span>
        )}
        <span className="text-figma-text font-roboto font-bold text-[14px] uppercase tracking-[0.08em] truncate w-full">
          {peekTitle}
        </span>
      </div>
      <span className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-figma-lime text-figma-panel text-xs font-roboto-mono uppercase tracking-wider font-bold flex-shrink-0">
        <span>Details</span>
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
          <path
            d="M1 5L5 1L9 5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </button>
  );
};
